import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { PlanBanner } from '@/components/billing/plan-banner'
import { NavLink, Outlet, useLocation, useMatches } from 'react-router'
import { ConsoleHeader } from '@/components/console/frame/header'
import { NAV_MOBILE } from '@/components/console/frame/nav'
import { Sidebar } from '@/components/console/frame/sidebar'
import { Skeleton } from '@/components/console/skeleton'
import { Icon } from '@/components/icons/icon'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { DemoProvider } from '@/lib/demo/demo-provider'
import { cn } from '@/lib/utils'

/** A route's `handle`: what the header calls the screen. */
export interface ScreenHandle {
  title: string
}

function isScreenHandle(handle: unknown): handle is ScreenHandle {
  return typeof handle === 'object' && handle !== null && typeof (handle as ScreenHandle).title === 'string'
}

/** The deepest matched route's title, e.g. "Agent builder" under /dashboard/agents/new. */
function useScreenTitle(): string {
  const matches = useMatches()
  for (let i = matches.length - 1; i >= 0; i--) {
    const { handle } = matches[i]
    if (isScreenHandle(handle)) return handle.title
  }
  return 'Overview'
}

/**
 * The tenant console's frame, from the design prototype (console.html `.app`):
 * sidebar, header, the screen, and on a phone the drawer and bottom bar.
 */
export function ConsoleFrame() {
  return (
    <DemoProvider>
      <Frame />
    </DemoProvider>
  )
}

function Frame() {
  const { pathname } = useLocation()
  const title = useScreenTitle()
  const [navOpen, setNavOpen] = useState(false)
  // Whatever opened the drawer (the burger or Menu) gets focus back when it closes.
  const opener = useRef<HTMLElement | null>(null)
  const openNav = useCallback(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    setNavOpen(true)
  }, [])
  const closeNav = useCallback(() => setNavOpen(false), [])

  useDocumentTitle(title)

  useEffect(() => {
    if (!navOpen) return
    // Into the drawer, at the current screen; Escape closes it, as in the prototype.
    document.querySelector<HTMLElement>('#side .nav-btn[aria-current="page"], #side .nav-btn')?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setNavOpen(false)
      opener.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [navOpen])

  return (
    <div className="app" id="app">
      <Sidebar open={navOpen} onNavigate={closeNav} />
      <div className="main">
        <ConsoleHeader title={title} navOpen={navOpen} onOpenNav={openNav} />
        <main className="content" id="content">
          <PlanBanner />
          <Suspense key={pathname} fallback={<ScreenSkeleton />}>
            <Outlet />
          </Suspense>
        </main>
        <nav className="mtab" aria-label="Quick navigation">
          {NAV_MOBILE.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={closeNav}>
              <Icon name={item.icon} />
              {item.label}
            </NavLink>
          ))}
          <button type="button" aria-expanded={navOpen} aria-controls="side" onClick={openNav}>
            <Icon name="menu" />
            Menu
          </button>
        </nav>
      </div>
      <div className={cn('scrim', navOpen && 'on')} onClick={closeNav} aria-hidden="true" />
    </div>
  )
}

/** While a screen's code loads: the prototype's loading treatment, a header and a panel. */
export function ScreenSkeleton() {
  return (
    <div className="screen on" aria-busy="true" aria-label="Loading">
      <div className="phead">
        <div style={{ display: 'grid', gap: 8 }}>
          <Skeleton height={26} width={260} />
          <Skeleton height={14} width={340} style={{ maxWidth: '100%' }} />
        </div>
      </div>
      <Skeleton height={180} />
    </div>
  )
}
